import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Phone, ArrowRight, Gift, X, Sparkles, CheckCircle2, XCircle, Timer, Lock, Mail, User } from 'lucide-react';
import { api } from '../api';
import { quickChallenges } from '../mock';
import SocialProofToasts from './SocialProofToasts';
import { useAuth } from '../context/AuthContext';

const SESSION_KEY = 'tbt_phone_capture_shown_v1';

const COUNTRY_CODES = [
  { code: '+91',  flag: 'IN',  label: 'India' },
  { code: '+1',   flag: 'US',  label: 'USA' },
  { code: '+44',  flag: 'UK',  label: 'UK' },
  { code: '+61',  flag: 'AU',  label: 'Australia' },
  { code: '+65',  flag: 'SG',  label: 'Singapore' },
  { code: '+971', flag: 'AE',  label: 'UAE' },
  { code: '+27',  flag: 'ZA',  label: 'South Africa' },
];

/**
 * Single-container acquisition funnel:
 *   phone → challenge (3 puzzles) → report → signup → plan & autopay → app
 * Never redirects to another page until the user is fully activated.
 */
export const PENDING_FUNNEL_KEY = 'tbt_pending_funnel_v1';

const PhoneCaptureModal = ({ autoOpen = true }) => {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('phone'); // phone | challenge | report | signup
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [fullMobile, setFullMobile] = useState('');

  useEffect(() => {
    // Wait for the session check: a stale/expired token in storage must not hide the funnel
    if (!autoOpen || !ready || user) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const t = setTimeout(() => setOpen(true), 400);
    return () => clearTimeout(t);
  }, [autoOpen, ready, user]);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setOpen(false);
  };

  // Progress bar tiers
  const stepOrder = ['phone', 'challenge', 'report', 'signup'];
  const stepIdx = stepOrder.indexOf(step);
  const showSocialProof = open && step === 'phone';

  // Modal width grows for content-heavier steps
  const widthCls = step === 'challenge' || step === 'report' ? 'max-w-lg' : 'max-w-md';

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
        <DialogContent
          data-testid="phone-capture-modal"
          className={`w-[calc(100vw-24px)] ${widthCls} bg-[#2A1810] border border-[#F4C430]/30 text-[#F5EDE0] p-0 overflow-hidden rounded-3xl [&>button:not([data-testid])]:hidden max-h-[92vh] overflow-y-auto`}
        >
          <DialogTitle className="sr-only">Get 7 Days Free Access</DialogTitle>
          <DialogDescription className="sr-only">
            Enter your mobile number, complete 3 quick brain challenges, unlock your Brain Report and get 7 days of free access to The Brain Theory.
          </DialogDescription>

          <button
            onClick={dismiss}
            aria-label="Close"
            data-testid="phone-capture-close"
            className="absolute top-3 right-3 h-9 w-9 rounded-full grid place-items-center border border-[#5a3d24] text-[#D9C9B3] hover:text-[#F4C430] hover:border-[#F4C430] z-10 bg-[#2A1810]/80 backdrop-blur"
          >
            <X size={16} />
          </button>

          {/* Progress bar across steps */}
          <div className="px-6 pt-6 sm:px-7 sm:pt-7">
            <div className="flex items-center gap-1.5">
              {stepOrder.map((s, i) => (
                <div
                  key={s}
                  className={`h-1.5 rounded-full flex-1 transition-all ${
                    i <= stepIdx ? 'bg-[#F4C430]' : 'bg-[#4a3221]'
                  }`}
                  data-testid={`funnel-progress-${s}`}
                />
              ))}
            </div>
          </div>

          {step === 'phone' && (
            <StepPhone
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              phone={phone}
              setPhone={setPhone}
              onSubmitted={(mobile) => { setFullMobile(mobile); setStep('challenge'); }}
              onDismiss={dismiss}
            />
          )}

          {step === 'challenge' && (
            <StepChallenges onDone={(results) => { setStep('report'); (window.__tbt_last_results = results); }} />
          )}

          {step === 'report' && (
            <StepReport
              results={window.__tbt_last_results || []}
              onContinue={() => setStep('signup')}
            />
          )}

          {step === 'signup' && (
            <StepSignup
              prefilledMobile={fullMobile}
              onSignedUp={() => {
                // Funnel answers are saved after autopay is set up (onboarding is gated on autopay)
                sessionStorage.setItem(PENDING_FUNNEL_KEY, JSON.stringify({ source: 'funnel', coupon: 'WELCOME' }));
                sessionStorage.setItem(SESSION_KEY, '1');
                setOpen(false);
                setTimeout(() => navigate('/choose-plan'), 200);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <SocialProofToasts active={showSocialProof} />
    </>
  );
};

// -------- Step 1: Phone -----------------------------------------------------
const StepPhone = ({ countryCode, setCountryCode, phone, setPhone, onSubmitted, onDismiss }) => {
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 8) { setErr('Please enter a valid mobile number'); return; }
    setLoading(true); setErr(null);
    try {
      const full = `${countryCode}${digits}`;
      await api.capturePhoneLead({ mobile: full, countryCode, source: 'homepage_hero' });
      sessionStorage.setItem('tbt_lead_mobile_v1', full);
      onSubmitted(full);
    } catch (e2) {
      setErr(e2?.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="p-6 sm:p-7 pt-4" data-testid="funnel-step-phone">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#3D2817] border border-[#F4C430]/40 px-3 py-1 text-[11px] text-[#F4C430]">
        <Sparkles size={12} /> Get 7 Days Free Access
      </div>

      <h2 className="mt-4 font-serif text-2xl sm:text-3xl leading-tight">
        <span className="text-white">Start </span>
        <span className="text-[#F4C430] italic">Free Challenges</span>
        <span className="text-white">.</span>
      </h2>
      <p className="mt-2 text-[#B8A990] text-sm">
        Enter your mobile number to begin. 3 quick brain challenges, then your{' '}
        <span className="text-[#F5EDE0] font-medium">Brain Report</span> — followed by{' '}
        <span className="text-[#F4C430] font-semibold">7 days free</span>.
      </p>

      <form onSubmit={submit} className="mt-5 space-y-3" noValidate>
        <div className="flex gap-2">
          <div className="relative shrink-0">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              data-testid="phone-capture-country"
              aria-label="Country code"
              className="appearance-none w-[92px] h-12 rounded-xl bg-[#3D2817] border border-[#5a3d24] text-[#F5EDE0] pl-3 pr-6 text-sm focus:outline-none focus:border-[#F4C430]"
            >
              {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
            </select>
            <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#8f7658]">▾</div>
          </div>
          <div className="relative flex-1">
            <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8f7658]" />
            <input
              data-testid="phone-capture-input"
              type="tel" inputMode="tel"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); if (err) setErr(null); }}
              placeholder="Enter your mobile number"
              className={`w-full h-12 rounded-xl bg-[#3D2817] border ${err ? 'border-red-400/60' : 'border-[#5a3d24]'} pl-10 pr-4 text-sm text-[#F5EDE0] placeholder:text-[#8f7658] focus:outline-none focus:border-[#F4C430]`}
              aria-invalid={!!err} autoComplete="tel" autoFocus
            />
          </div>
        </div>
        {err && (
          <div className="flex items-start gap-1.5 text-xs text-red-300" role="alert" data-testid="phone-capture-error">
            <span className="mt-0.5 inline-block h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        <Button
          type="submit" disabled={loading}
          data-testid="phone-capture-submit"
          className="w-full rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12"
        >
          {loading ? 'Just a sec…' : (<><span>Start </span><span className="ml-1">Free Challenges</span><ArrowRight size={16} className="ml-1" /></>)}
        </Button>

        <div className="rounded-xl bg-[#3D2817] border border-[#4a3221] p-3 flex items-start gap-2.5">
          <Gift size={14} className="text-[#F4C430] mt-0.5 shrink-0" />
          <div className="text-xs text-[#B8A990]">
            <span className="text-[#F5EDE0]">7 days free</span> · Unlock your Brain Report · Then pick a plan with autopay — cancel anytime.
          </div>
        </div>

        <button
          type="button" onClick={onDismiss}
          data-testid="phone-capture-skip"
          className="w-full text-center text-xs text-[#8f7658] hover:text-[#F4C430] py-1"
        >
          Not now, I'll explore first
        </button>
      </form>
    </div>
  );
};

// -------- Step 2: Challenges (3 puzzles in-container) -----------------------
const StepChallenges = ({ onDone }) => {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const puzzle = quickChallenges[idx];
  const [seconds, setSeconds] = useState(puzzle.seconds);
  const timeoutRef = useRef(null);

  const isMemory = !!puzzle.memorizeSeconds;
  const [studyLeft, setStudyLeft] = useState(puzzle.memorizeSeconds || 0);
  useLayoutEffect(() => {
    setSelected(null); setStatus('idle');
    setSeconds(quickChallenges[idx].seconds);
    setStudyLeft(quickChallenges[idx].memorizeSeconds || 0);
  }, [idx]);

  // Memory: show the sequence for a few seconds, then hide it and ask the question
  const studying = isMemory && studyLeft > 0;
  useEffect(() => {
    if (!studying) return undefined;
    const t = setTimeout(() => setStudyLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [studying, studyLeft]);

  const timerVisible = idx >= 1 && !studying; // hide timer on first, show from 2nd on

  useEffect(() => {
    if (status !== 'idle' || !timerVisible) return;
    if (seconds <= 0) { setStatus('timeup'); return; }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, status, timerVisible]);

  useEffect(() => {
    if (status === 'idle') return;
    const wasCorrect = status === 'correct';
    const nextResults = [...results, { category: puzzle.category, correct: wasCorrect }];
    setResults(nextResults);
    timeoutRef.current = setTimeout(() => {
      if (idx < quickChallenges.length - 1) setIdx(idx + 1);
      else onDone(nextResults);
    }, 1000);
    return () => timeoutRef.current && clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const choose = (opt) => {
    if (status !== 'idle') return;
    setSelected(opt);
    setStatus(opt === puzzle.answer ? 'correct' : 'wrong');
  };

  return (
    <div className="p-6 sm:p-7 pt-4" data-testid="funnel-step-challenge">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#3D2817] border border-[#4a3221] px-3 py-1 text-[11px] text-[#F4C430]">
          {puzzle.category} · Challenge {idx + 1} of {quickChallenges.length}
        </div>
        {timerVisible && (
          <div className="text-[11px] text-[#B8A990] inline-flex items-center gap-1"><Timer size={12} /> 00:{String(Math.max(seconds, 0)).padStart(2, '0')}</div>
        )}
      </div>

      {studying ? (
        <div className="mt-4" data-testid="funnel-memory-study">
          <div className="text-sm text-[#B8A990]">{puzzle.studyText} — they'll disappear in <span className="text-[#F4C430] font-semibold" data-testid="funnel-memory-countdown">{studyLeft}s</span></div>
          <p className="mt-3 text-2xl sm:text-3xl text-[#F5EDE0] font-serif leading-snug">{puzzle.prompt}</p>
          <div className="mt-5 h-1.5 rounded-full bg-[#3D2817] overflow-hidden">
            <div className="h-full bg-[#F4C430] transition-[width] duration-1000 ease-linear" style={{ width: `${(studyLeft / puzzle.memorizeSeconds) * 100}%` }} />
          </div>
          <button type="button" onClick={() => setStudyLeft(0)} data-testid="funnel-memory-ready"
            className="mt-4 w-full rounded-full border border-[#5a3d24] h-11 text-sm text-[#F5EDE0] hover:border-[#F4C430] hover:text-[#F4C430]">
            I've memorised it
          </button>
        </div>
      ) : (
      <>
      <h3 className="mt-4 text-[#F5EDE0] text-base font-serif" data-testid="funnel-challenge-question">{puzzle.question}</h3>
      {!isMemory && <p className="mt-2 text-2xl sm:text-3xl text-[#F5EDE0] font-serif">{puzzle.prompt}</p>}

      <div className="mt-5 grid grid-cols-2 gap-2.5" data-testid="funnel-challenge-options">
        {puzzle.options.map((opt) => {
          const isSel = selected === opt;
          const isRight = status !== 'idle' && opt === puzzle.answer;
          const isWrongPick = status === 'wrong' && isSel;
          return (
            <button
              key={opt}
              onClick={() => choose(opt)}
              disabled={status !== 'idle'}
              data-testid={`funnel-challenge-opt-${opt}`}
              className={`rounded-xl border h-12 text-base text-[#F5EDE0] transition-all
                ${isRight ? 'bg-[#F4C430] text-[#2A1810] border-[#F4C430]' : ''}
                ${isWrongPick ? 'border-red-400 text-red-300' : ''}
                ${status === 'idle' ? 'border-[#5a3d24] hover:border-[#F4C430] hover:bg-[#4a3221] active:scale-[0.98]' : 'border-[#5a3d24]'}
              `}
            >
              {opt}
            </button>
          );
        })}
      </div>

      <div className="mt-3 min-h-[24px] text-xs sm:text-sm">
        {status === 'correct' && (<span className="inline-flex items-center gap-1.5 text-[#F4C430]"><CheckCircle2 size={16} /> Nice! Moving on…</span>)}
        {status === 'wrong'   && (<span className="inline-flex items-center gap-1.5 text-red-300"><XCircle size={16} /> Not quite — keep going</span>)}
        {status === 'timeup'  && (<span className="inline-flex items-center gap-1.5 text-[#E8B923]"><Timer size={16} /> Time up — next question</span>)}
        {status === 'idle'    && (<span className="text-[#B8A990]">Instant feedback · we track how you think</span>)}
      </div>
      </>
      )}
    </div>
  );
};

// -------- Step 3: Brain Report ---------------------------------------------
const StepReport = ({ results, onContinue }) => {
  const correct = results.filter((r) => r.correct).length;
  const total = Math.max(1, results.length);
  const score = Math.round((correct / total) * 100);

  return (
    <div className="p-6 sm:p-7 pt-4" data-testid="funnel-step-report">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#3D2817] border border-[#F4C430]/40 px-3 py-1 text-[11px] text-[#F4C430]">
        <Sparkles size={12} /> Your Brain Report Is Ready!
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl leading-tight">
        Discover how your <span className="italic text-[#F4C430]">brain thinks</span>.
      </h2>
      <p className="mt-2 text-[#B8A990] text-sm">
        You got <span className="text-[#F5EDE0] font-semibold">{correct}/{results.length}</span> right — here's a snapshot of your cognitive strengths.
      </p>

      {/* Score card */}
      <div className="mt-5 rounded-2xl bg-[#3D2817] border border-[#4a3221] p-4 flex items-center gap-4">
        <div className="relative h-20 w-20 shrink-0">
          <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#4a3221" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F4C430" strokeWidth="3"
              strokeDasharray={`${score}, 100`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="font-serif text-2xl text-[#F5EDE0]">{score}</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-[#B8A990] uppercase tracking-widest">Brain Snapshot</div>
          <div className="mt-0.5 text-[#F5EDE0] font-medium">{score >= 66 ? 'Sharp thinker' : score >= 33 ? 'Curious explorer' : 'Warming up'}</div>
          <div className="mt-0.5 text-xs text-[#B8A990]">Save your results and unlock your full report</div>
        </div>
      </div>

      {/* Per-category breakdown */}
      <div className="mt-4 grid grid-cols-3 gap-2" data-testid="funnel-report-breakdown">
        {results.map((r, i) => (
          <div key={i} className="rounded-xl border border-[#4a3221] bg-[#3D2817] p-2.5 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#B8A990]">{r.category}</div>
            <div className={`mt-1 text-sm font-semibold ${r.correct ? 'text-[#F4C430]' : 'text-red-300'}`}>
              {r.correct ? '✓ Correct' : '✕ Missed'}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-xl bg-[#3D2817] border border-[#F4C430]/40 p-3 flex items-start gap-2.5">
        <Gift size={14} className="text-[#F4C430] mt-0.5 shrink-0" />
        <div className="text-xs text-[#B8A990]">
          <span className="text-[#F5EDE0] font-medium">Save your results + get 7 days free access</span> — then your chosen plan, cancel anytime.
        </div>
      </div>

      <Button
        onClick={onContinue}
        data-testid="funnel-report-continue"
        className="mt-4 w-full rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12"
      >
        Save My Report & Continue <ArrowRight size={16} className="ml-1" />
      </Button>
    </div>
  );
};

// -------- Step 4: Signup (mobile prefilled) --------------------------------
const StepSignup = ({ prefilledMobile, onSignedUp }) => {
  const { signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [err, setErr] = useState({});
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!name.trim()) next.name = 'Please enter your name';
    if (!email.match(/.+@.+\..+/)) next.email = 'Please enter a valid email address';
    if (!password || password.length < 6) next.password = 'At least 6 characters';
    setErr(next);
    if (Object.keys(next).length) return;

    setLoading(true);
    try {
      const user = await signup({ name: name.trim(), email: email.trim(), password, mobile: prefilledMobile });
      onSignedUp(user);
    } catch (e2) {
      const msg = (e2?.message || '').toLowerCase();
      if (msg.includes('mobile')) setErr({ form: e2.message });
      else if (msg.includes('email') || msg.includes('already') || msg.includes('registered')) setErr({ email: 'This email is already registered — please log in from the top menu.' });
      else setErr({ form: e2?.message || 'Could not create account.' });
      setLoading(false);
    }
  };

  return (
    <div className="p-6 sm:p-7 pt-4" data-testid="funnel-step-signup">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#3D2817] border border-[#F4C430]/40 px-3 py-1 text-[11px] text-[#F4C430]">
        <Sparkles size={12} /> Save your results & unlock 7 days free
      </div>
      <h2 className="mt-3 font-serif text-2xl leading-tight">
        One last step to <span className="italic text-[#F4C430]">save your progress</span>
      </h2>
      <p className="mt-1 text-[#B8A990] text-sm">Mobile <span className="text-[#F5EDE0]">{prefilledMobile}</span> · takes 15 seconds</p>

      <form onSubmit={submit} className="mt-4 space-y-3" noValidate>
        <FieldRow icon={User} placeholder="Your name" value={name} onChange={setName} err={err.name} testid="funnel-signup-name" autoComplete="name" />
        <FieldRow icon={Mail} type="email" placeholder="Email" value={email} onChange={setEmail} err={err.email} testid="funnel-signup-email" autoComplete="email" />
        <div className="relative">
          <FieldRow icon={Lock} type={show ? 'text' : 'password'} placeholder="Create a password (min 6 chars)"
            value={password} onChange={setPassword} err={err.password} testid="funnel-signup-password" autoComplete="new-password" />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-1 top-1 h-10 w-10 grid place-items-center text-[#8f7658] hover:text-[#F4C430]" aria-label="Toggle password visibility">
            {show ? '🙈' : '👁'}
          </button>
        </div>
        {err.form && (
          <div className="rounded-xl border border-red-400/50 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid="funnel-signup-form-error">{err.form}</div>
        )}

        <Button type="submit" disabled={loading}
          data-testid="funnel-signup-submit"
          className="w-full rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12">
          {loading ? 'Creating account…' : (<>Continue <ArrowRight size={16} className="ml-1" /></>)}
        </Button>

        <p className="text-[10px] text-[#8f7658] text-center">By continuing you agree to our Terms & Privacy Policy.</p>
      </form>
    </div>
  );
};

const FieldRow = ({ icon: Icon, err, testid, onChange, value, ...rest }) => (
  <div>
    <div className="relative">
      <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8f7658]" />
      <input
        {...rest}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        aria-invalid={!!err}
        className={`w-full h-12 rounded-xl bg-[#3D2817] border ${err ? 'border-red-400/60' : 'border-[#5a3d24]'} pl-10 pr-10 text-sm text-[#F5EDE0] placeholder:text-[#8f7658] focus:outline-none focus:border-[#F4C430]`}
      />
    </div>
    {err && (
      <div className="mt-1.5 flex items-start gap-1.5 text-xs text-red-300" role="alert">
        <span className="mt-0.5 inline-block h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
        <span>{err}</span>
      </div>
    )}
  </div>
);

export default PhoneCaptureModal;
